import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Briefcase, CalendarDays, Handshake, ExternalLink } from "lucide-react";
import { Panel } from "@/components/shared";
import { api } from "@/lib/api";

const TYPES = [["", "All"], ["new_jobs", "New jobs"], ["hiring_event", "Hiring events"], ["mna", "M&A"]];
const ICON = { new_jobs: Briefcase, hiring_event: CalendarDays, mna: Handshake };
const when = (iso) => new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

const Item = ({ i, fresh }) => {
  const Icon = ICON[i.type];
  return (
    <li className={`flex gap-3 border-b py-3 last:border-0 transition-colors duration-700 ${fresh ? "bg-amber-50" : ""}`} data-testid={`feed-item-${i.id}`}>
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
      <div className="min-w-0 flex-1">
        {i.type === "new_jobs" ? (
          <Link to={`/app/jobs?q=${encodeURIComponent(i.company)}`} className="hover:text-primary">
            <p className="text-sm"><span className="font-semibold">{i.company}</span> posted {i.count} new role{i.count > 1 ? "s" : ""} in {i.state_name}{i.senior_count ? ` · ${i.senior_count} manager+` : ""}</p>
            <p className="truncate text-xs text-muted-foreground">{i.titles.join(" · ")}</p>
          </Link>
        ) : (
          <a href={i.url} target="_blank" rel="noopener noreferrer" className="hover:text-primary">
            <p className="text-sm font-medium">{i.title} <ExternalLink className="inline h-3 w-3" /></p>
            <p className="text-xs text-muted-foreground">{i.source}{i.state_name ? ` · ${i.state_name}` : ""}{i.companies?.length ? ` · ${i.companies.join(", ")}` : ""}</p>
          </a>
        )}
      </div>
      <time className="shrink-0 text-xs text-muted-foreground" dateTime={i.at}>{when(i.at)}</time>
    </li>
  );
};

export const HiringFeed = () => {
  const [type, setType] = useState("");
  const [state, setState] = useState("");
  const [d, setD] = useState(null);
  const seen = useRef(new Set());
  const [fresh, setFresh] = useState(new Set());
  useEffect(() => {
    seen.current = new Set();
    let alive = true;
    const load = () => api.get("/feed", { params: { type, state } }).then((r) => {
      if (!alive) return;
      const first = seen.current.size === 0;
      const added = new Set(r.data.items.filter((i) => !seen.current.has(i.id)).map((i) => i.id));
      r.data.items.forEach((i) => seen.current.add(i.id));
      setFresh(first ? new Set() : added);
      setD(r.data);
    }).catch(() => {});
    load();
    const t = setInterval(load, 30000);
    return () => { alive = false; clearInterval(t); };
  }, [type, state]);
  return (
    <Panel className="lg:col-span-12" testid="hiring-feed">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 text-lg font-semibold"><span className="relative flex h-2.5 w-2.5"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" /><span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500" /></span>Live hiring feed</h2>
        <div className="flex flex-wrap gap-2">
          {TYPES.map(([k, t]) => (
            <button key={k} onClick={() => setType(k)} className={`rounded-full border px-3 py-1 text-xs transition-colors ${type === k ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`} data-testid={`feed-type-${k || "all"}`}>{t}</button>
          ))}
          <select className="h-7 rounded-full border px-2 text-xs" value={state} onChange={(e) => setState(e.target.value)} data-testid="feed-state-select">
            <option value="">All US states</option>
            {d && Object.entries(d.states).sort((a, b) => a[1].localeCompare(b[1])).map(([c, n]) => <option key={c} value={c}>{n}</option>)}
          </select>
        </div>
      </div>
      {!d ? <p className="text-sm text-muted-foreground">Loading…</p> : (
        <>
          {d.items.length === 0 ? <p className="py-6 text-center text-sm text-muted-foreground" data-testid="feed-empty">No activity in the last 3 days for this filter.</p> : (
            <ul className="max-h-[420px] overflow-y-auto pr-1" data-testid="feed-list">{d.items.map((i) => <Item key={i.id} i={i} fresh={fresh.has(i.id)} />)}</ul>
          )}
          <p className="mt-3 text-[11px] text-muted-foreground" data-testid="feed-footnote">
            New jobs use each employer's own posting time, US locations only. Updates every 30 seconds.
            {d.news_enabled ? " Hiring-event and M&A headlines link to the original news source." : " Hiring-event and M&A news is coming soon."}
          </p>
        </>
      )}
    </Panel>
  );
};
